import { goSolidApi } from "@/composables/useGoSolidApi";

import type {
  ForwardInventoryResponse,
  ForwardOfferRequest,
  OfferResponse,
  SpotInventoryResponse,
  SpotOfferRequest,
  SpotSelectionRequest,
  SpotSheetResponse,
} from "./solderaInventoryTypes";

const BASE = "/api/soldera-inventory";

/**
 * Soldera Inventory endpoints (app/soldera_inventory/api/), one shape for spot and forward: list what
 * is on offer at the buyer's price, then send an offer. Every call answers 403 unless the account has
 * the `realtime_soldera_inventory` flag; offers answer 400 when a row or mandate is gone or no longer
 * clears its floor at this price, and 429 past the hourly cap they share.
 */
export const solderaInventoryApi = {
  spot: {
    /** GET …/spot/items — the sellable spot certificates, minimum-price rows only once `unitPrice` clears them. */
    listItems: (unitPrice: number) =>
      goSolidApi.get<SpotInventoryResponse>(`${BASE}/spot/items`, {
        params: { unit_price: unitPrice.toFixed(3) },
      }),
    /** POST …/spot/offers — drafts a sales transaction with its own sheet, shared to the account's email. */
    createOffer: (data: SpotOfferRequest) =>
      goSolidApi.post<OfferResponse>(`${BASE}/spot/offers`, data),
    /** POST …/spot/sheets — the selection's tender sheet, shared read-only with the account's email. */
    createSheet: (data: SpotSelectionRequest) =>
      goSolidApi.post<SpotSheetResponse>(`${BASE}/spot/sheets`, data),
  },
  forward: {
    /** GET …/forward/mandates — the forward mandates over `periods`, mandates only once `unitPrice` clears their floor. */
    listMandates: (periods: string[], unitPrice: number) =>
      goSolidApi.get<ForwardInventoryResponse>(`${BASE}/forward/mandates`, {
        // axios sends an array as `periods[]=…` repeated, which is what the query model parses.
        params: { periods, unit_price: unitPrice.toFixed(3) },
      }),
    /** POST …/forward/offers — drafts a forward deal to the buyer's counterparty for staff to finish. */
    createOffer: (data: ForwardOfferRequest) =>
      goSolidApi.post<OfferResponse>(`${BASE}/forward/offers`, data),
  },
};
